import {
  Box,
  Popover,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  useMediaQuery,
} from "@mui/material";
import UIButton from "components/ui/form/UIButton";
import UIDatePicker from "components/datepicker/UIDatePicker";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { FormError, genderOptionList } from "config/helpers/common_helper";
import { useEffect, useState } from "react";
import { parseName } from "humanparser";
import { formatDate } from "config/helpers/date_helper";
import { CREATE_AUDIT_EVENT, UPDATE_PATIENT } from "adapters/ehr.service";
import { toast } from "react-toastify";
import { patientObject } from "redux/actions/PatientActions";
import { useDispatch, useSelector } from "react-redux";

const PatientUpdateDrawer = ({
  setIsEditPatientDirty = () => {},
  ...props
}) => {
  const reduxDispatch = useDispatch();
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery("(max-width:767px)");
  const formInitialValues = {
    patientName: props?.patientObj?.name?.[0]?.text || "",
    dob: props?.patientObj?.birthDate
      ? dayjs(props?.patientObj?.birthDate)
      : null,
    gender: props?.patientObj?.gender || "",
  };
  const theme = useSelector((state) => state?.app.theme);

  const formData = useForm({
    defaultValues: formInitialValues,
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    reset,
  } = props?.formControlObj || formData;

  const PractitionerID = useSelector(
    (state) => state?.auth?.practitionerData?.id
  );
  const formCurrentValues = watch();

  const hasFormValuesChanged = () => {
    const initialValuesString = JSON.stringify(formInitialValues);
    const currentValuesString = JSON.stringify(formCurrentValues);

    return initialValuesString !== currentValuesString;
  };

  const [isLoading, setIsLoading] = useState({ saveBtn: false });
  const isTab = useMediaQuery(
    (theme) => theme.breakpoints.up("sm") && theme.breakpoints.down("lg")
  );

  useEffect(() => {
    setIsEditPatientDirty?.(isDirty);
  }, [isDirty]);

  const handleOnSavePatient = async (data) => {
    if (!hasFormValuesChanged()) {
      // Display toast notification indicating no changes were made
      toast.warning(`${i18n.t("Patient_Screen.NO_CHANGES_TOAST")}`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 4000,
      });
      return; // Exit early if no changes were detected
    }
    setIsLoading(true);

    // Create a deep copy of props.patientObj to avoid mutating the original state
    let payload = JSON.parse(JSON.stringify(props.patientObj));

    if (Object.hasOwn(payload, "encounterReset")) {
      delete payload.encounterReset;
    }

    const attrs = parseName(data?.patientName);
    const { salutation, firstName, middleName, lastName, suffix } = attrs;

    const givenNames = middleName ? [firstName, middleName] : [firstName];

    const nameList = [
      {
        use: "official",
        family: lastName,
        given: givenNames,
        prefix: salutation ? [salutation.trim()] : [],
        suffix: suffix ? [suffix.trim()] : [],
        text: data?.patientName?.trim(),
      },
    ];

    if (data?.patientName) {
      payload.name = nameList;
    }
    // debugger;
    if (data?.dob) {
      payload.birthDate = formatDate(data?.dob, "-", "YYYY-MM-DD");
    } else {
      delete payload.birthDate;
    }

    if (data?.gender) {
      payload.gender = data?.gender;
    }

    delete payload.clinicalImpressionReset;

    try {
      setIsLoading({ saveBtn: true });
      const response = await UPDATE_PATIENT({
        resourceType: "Patient",
        ...payload,
      });
      CREATE_AUDIT_EVENT({
        practitionerID: `Practitioner/${PractitionerID}`,
        patientName: payload.name[0].text,
        type: { display: "Update", code: "110121" },
        // reason: `Patient Modified: ${payload?.name?.[0]?.text}`,
        reason: JSON.stringify({
          msg_code: "PATIENT_MODIFIED",
          data: payload?.name?.[0]?.text
        })
      });

      toast.success(i18n.t("common.PATIENT_UPDATED_SUCCESSFULLY"));
      reduxDispatch(patientObject(response?.data));
      if (props?.rendererFunction) {
        props?.rendererFunction();
      }
    } catch (error) {
      console.error("Failed to update patient", error);
      toast.error("Failed to update patient");
    } finally {
      props?.close({ directCancel: true });
      reset({ ...data });
    }
  };

  const isNotEmpty = (value) => value?.trim() !== "";
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Typography variant="h5">{i18n.t("common.EDIT")}</Typography>
        <div>
          <Controller
            control={control}
            name="patientName"
            rules={{
              required: i18n.t(
                "patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"
              ),
              validate: {
                isNotEmpty: (value) =>
                  isNotEmpty(value) ||
                  i18n.t("patient2.PATIENT_FIRST_NAME_LAST_NAME_IS_REQUIRED"),
                isAlphabetical: (value) =>
                  /^(?=\p{L})[\p{L}0-9., ]*$/u.test(value) ||
                  i18n.t("error_messages.INVALID_NAME"), // Add an error message for invalid name
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus={!isTab}
                // autoFocus
                autoComplete="off"
                placeholder={i18n.t(
                  "Patient_Screen.PATIENT_FIRST_NAME_LAST_NAME"
                )}
                variant="outlined"
                fullWidth
                size="small"
                error={errors?.patientName?.message}
                sx={{
                  "& .MuiInputBase-input::placeholder": {
                    opacity: 0.8,
                    fontWeight: 500,
                    color: theme === "dark" ? "#aaa" : "#aaa",
                  },
                  // Style for the asterisk
                  "& .MuiInputBase-root": {
                    "&::after": {
                      content: `'${field?.value ? "" : "*"}'`,
                      color: "red", // Set asterisk color to red
                      position: "absolute",
                      left: "5px",
                    },
                  },
                  fontSize: isMobile ? "1.2vh" : "",
                }}
              />
            )}
          />
          <FormError
            sx={{ fontSize: isMobile ? "1.2vh" : "" }}
            error={errors?.patientName?.message}
          />
        </div>
        {/* <TextFieldWithTopLabelComponent
                        {...PatientNameValidationObject}
                        onChange={handleOnChange}
                        width={"100%"}
                        sx={{
                          ".MuiInputBase-root": {
                            borderRadius: "8px",
                          },
                        }}
                      /> */}
        <Grid
          container
          spacing={1}
          // className="d-flex gap-2 align-items-start"
        >
          <Grid
            sx={{ marginTop: isMobile ? "-1.2vh" : "" }}
            item
            xs={isMobile ? 12 : 6}
          >
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Select
                  fullWidth
                  {...field}
                  size="small"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      color: "black",
                      opacity: 0.8,
                    },
                    // Style for the asterisk
                    "& .MuiInputBase-input": {
                      "&::after": {
                        content: `'${
                          field?.value ? "" : i18n.t("Patient_Screen.GENDER")
                        }'`,
                        // color: "red", // Set asterisk color to red
                        position: "absolute",
                        left: "10px",
                      },
                    },
                    fontSize: isMobile ? "1.2vh" : "",
                  }}
                >
                  {genderOptionList?.map((item, id) => (
                    <MenuItem
                      value={item.value}
                      key={id}
                      title={i18n.t(item.label)}
                    >
                      {i18n.t(item.label)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>
          <Grid item sm={isMobile ? 12 : 6} xs={isMobile ? 12 : 6}>
            <Controller
              control={control}
              name="dob"
              rules={{
                validate: (value) => {
                  if (value && !dayjs(value, { strict: true })?.isValid()) {
                    return i18n?.t('settings2.INVALID_DATE_FORMAT');;
                  }
                },
                required: i18n.t("error_messages.DOB_REQUIRED"),
              }}
              render={({ field }) => (
                <UIDatePicker
                  {...field}
                  maxDate={dayjs()}
                  placeholder={i18n.t("Patient_Screen.DATE_OF_BIRTH")}
                  size={"small"}
                  sx={{
                    "& .MuiInputBase-root": {
                      "&::after": {
                        content: `'${field?.value ? "" : "*"}'`,
                        color: "red", // Set asterisk color to red
                        position: "absolute",
                        left: "5px",
                      },
                    },
                  }}
                  error={errors.dob}
                  fullWidth
                  placement={isMobile ? "auto" : "left"}
                />
              )}
            />
            <FormError
              sx={{ fontSize: isMobile ? "1.2vh" : "" }}
              error={errors?.dob?.message}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: "10px",
          }}
        >
          <UIButton
            fullWidth
            callback={handleSubmit(handleOnSavePatient)}
            variant="contained"
            text={i18n.t("common.UPDATE")}
            loading={isLoading?.saveBtn}
            // loadingIndicator={"Creating patient..."}
          />

          <UIButton
            fullWidth
            callback={() => {
              props?.close({ directCancel: true });
              setIsEditPatientDirty?.(false);
            }}
            variant="outlined"
            text={i18n.t("Patient_Screen.CANCEL")}
            // loading={isLoading}
            // loadingIndicator={"Creating patient..."}
          />
        </Box>
      </Box>
    </>
  );
};

export default PatientUpdateDrawer;
